import React, { useState, useEffect } from 'react';
import { FiUsers, FiSearch, FiDownload, FiCheckCircle, FiMail, FiPhone, FiRefreshCw } from 'react-icons/fi';
import EventService from '../services/EventService';

const Attendees = () => {
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [attendees, setAttendees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoadingId, setActionLoadingId] = useState(null);

    const fetchAttendees = async () => {
        setLoading(true);
        try {
            let res = await EventService.getAttendees().catch(() => null);
            if (!res) {
                const token = localStorage.getItem('partner_token') || localStorage.getItem('token') || localStorage.getItem('admin_token') || '';
                const apiRes = await fetch('https://api.tapcoe.com/api/v1/events/partners/attendees', {
                    headers: {
                        'Content-Type': 'application/json',
                        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                    }
                }).catch(() => null);
                if (apiRes && apiRes.ok) {
                    res = await apiRes.json();
                }
            }
            const list = res?.attendees || res?.data?.attendees || (Array.isArray(res) ? res : []);
            setAttendees(Array.isArray(list) ? list : []);
        } catch (err) {
            console.error('Error loading attendees:', err);
            setAttendees([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAttendees();
    }, []);

    const handleToggleCheckIn = async (attendee) => {
        setActionLoadingId(attendee.id);
        try {
            const res = await EventService.toggleCheckIn(attendee.id);
            if (res?.success) {
                setAttendees(prev => prev.map(a => {
                    if (a.id === attendee.id) {
                        return {
                            ...a,
                            checkInStatus: res.checkInStatus || (a.checkInStatus === 'CHECKED_IN' ? 'REGISTERED' : 'CHECKED_IN'),
                            checkInTime: res.checkInTime || (a.checkInStatus === 'CHECKED_IN' ? '-' : new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }))
                        };
                    }
                    return a;
                }));
            }
        } catch (err) {
            console.error('Error toggling check-in:', err);
        } finally {
            setActionLoadingId(null);
        }
    };

    const filtered = attendees.filter(a => {
        const matchesSearch = (a.name || '').toLowerCase().includes(search.toLowerCase()) ||
            (a.email || '').toLowerCase().includes(search.toLowerCase()) ||
            (a.phone || '').toLowerCase().includes(search.toLowerCase()) ||
            (a.ticketId || '').toLowerCase().includes(search.toLowerCase());
        const matchesStatus = statusFilter === 'ALL' || a.checkInStatus === statusFilter;
        return matchesSearch && matchesStatus;
    });

    const exportCSV = () => {
        const headers = "Attendee ID,Name,Email,Phone,Event,TicketType,PaymentStatus,CheckInStatus,CheckInTime\n";
        const rows = filtered.map(a => `"${a.ticketId || a.id}","${a.name}","${a.email}","${a.phone}","${a.eventTitle || 'Event'}","${a.ticketType}","${a.paymentStatus}","${a.checkInStatus}","${a.checkInTime}"`).join("\n");
        const blob = new Blob([headers + rows], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'attendees_list.csv';
        link.click();
    };

    return (
        <div className="max-w-6xl mx-auto space-y-6 text-left pb-20">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white border border-slate-200 p-6 rounded-2xl shadow-sm">
                <div>
                    <h1 className="text-2xl font-black text-slate-900 tracking-tight flex items-center gap-2">
                        <FiUsers className="text-purple-700" /> Attendee Management
                    </h1>
                    <p className="text-xs font-medium text-slate-500 mt-1">Real-time registered participants & QR scan check-ins</p>
                </div>
                <div className="flex items-center gap-2 self-start md:self-auto">
                    <button
                        onClick={fetchAttendees}
                        className="p-2.5 rounded-xl bg-slate-100 text-slate-600 hover:text-slate-900 hover:bg-slate-200 border border-slate-200 text-xs font-bold transition-all shadow-sm"
                        title="Refresh List"
                    >
                        <FiRefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                    <button
                        onClick={exportCSV}
                        className="px-4 py-2.5 rounded-xl bg-purple-700 hover:bg-purple-800 text-white text-xs font-bold transition-all flex items-center gap-2 shadow-sm"
                    >
                        <FiDownload className="w-4 h-4" /> Export CSV
                    </button>
                </div>
            </div>

            {/* Search & Filter Bar */}
            <div className="bg-white border border-slate-200 p-4 rounded-2xl flex flex-col md:flex-row items-center justify-between gap-4 shadow-sm">
                <div className="relative w-full md:w-80">
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search by name, email, or ticket ID..."
                        className="w-full bg-white border border-slate-300 rounded-xl py-2.5 pl-10 pr-4 text-xs text-slate-900 placeholder-slate-400 outline-none focus:border-purple-600 font-medium"
                    />
                    <FiSearch className="absolute left-3 top-3 text-slate-400 w-4 h-4" />
                </div>

                <div className="flex items-center gap-2 w-full md:w-auto overflow-x-auto">
                    {['ALL', 'CHECKED_IN', 'REGISTERED', 'CANCELLED'].map((st) => (
                        <button
                            key={st}
                            onClick={() => setStatusFilter(st)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap ${statusFilter === st ? 'bg-purple-700 text-white shadow-xs' : 'bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200'
                                }`}
                        >
                            {st}
                        </button>
                    ))}
                </div>
            </div>

            {/* Attendees Table */}
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                {loading ? (
                    <div className="p-12 text-center text-slate-500 text-xs font-medium space-y-3">
                        <FiRefreshCw className="w-6 h-6 animate-spin mx-auto text-purple-700" />
                        <p>Loading real-time event attendees...</p>
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="p-12 text-center text-slate-500 text-xs space-y-2">
                        <FiUsers className="w-8 h-8 mx-auto text-slate-300" />
                        <p className="font-extrabold text-slate-900">No attendees found</p>
                        <p className="text-[11px] text-slate-500 font-medium">
                            {search || statusFilter !== 'ALL'
                                ? 'Try clearing search or filter settings.'
                                : 'When participants book tickets for your events, they will appear here live.'}
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs text-slate-700">
                            <thead className="bg-slate-100/70 text-slate-600 font-extrabold uppercase tracking-wider text-[11px] border-b border-slate-200">
                                <tr>
                                    <th className="p-4">Attendee</th>
                                    <th className="p-4">Contact</th>
                                    <th className="p-4">Ticket Type</th>
                                    <th className="p-4">Payment</th>
                                    <th className="p-4">Check-in Status</th>
                                    <th className="p-4">Check-in Time</th>
                                    <th className="p-4 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 bg-white">
                                {filtered.map((a) => (
                                    <tr key={a.id} className="hover:bg-slate-50 transition-colors">
                                        <td className="p-4">
                                            <div className="font-bold text-slate-900">{a.name}</div>
                                            <div className="text-[10px] font-mono text-purple-700 font-bold mt-0.5">{a.ticketId || a.id}</div>
                                        </td>
                                        <td className="p-4 text-slate-600 space-y-0.5 font-medium">
                                            <div className="flex items-center gap-1"><FiMail className="w-3 h-3 text-slate-400" /> {a.email}</div>
                                            <div className="flex items-center gap-1"><FiPhone className="w-3 h-3 text-slate-400" /> {a.phone}</div>
                                        </td>
                                        <td className="p-4 font-bold text-purple-900">
                                            <div>{a.ticketType}</div>
                                            <div className="text-[10px] text-slate-500 font-normal">{a.eventTitle}</div>
                                        </td>
                                        <td className="p-4">
                                            <span className={`px-2.5 py-1 rounded text-[10px] font-extrabold border ${a.paymentStatus === 'PAID' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-blue-50 text-blue-800 border-blue-200'
                                                }`}>
                                                {a.paymentStatus}
                                            </span>
                                        </td>
                                        <td className="p-4">
                                            <span className={`px-2.5 py-1 rounded-full text-[10px] font-extrabold flex items-center gap-1.5 w-max border ${a.checkInStatus === 'CHECKED_IN'
                                                    ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                                                    : 'bg-slate-100 text-slate-700 border-slate-200'
                                                }`}>
                                                {a.checkInStatus === 'CHECKED_IN' && <FiCheckCircle className="w-3 h-3 text-emerald-600" />}
                                                {a.checkInStatus}
                                            </span>
                                        </td>
                                        <td className="p-4 text-slate-600 font-medium">{a.checkInTime}</td>
                                        <td className="p-4 text-right">
                                            <button
                                                disabled={actionLoadingId === a.id}
                                                onClick={() => handleToggleCheckIn(a)}
                                                className="px-3 py-1.5 rounded-lg bg-slate-100 border border-slate-300 hover:bg-purple-700 hover:text-white disabled:opacity-50 text-slate-900 font-bold text-[11px] transition-colors shadow-xs"
                                            >
                                                {actionLoadingId === a.id ? 'Updating...' : a.checkInStatus === 'CHECKED_IN' ? 'Undo Check-in' : 'Check In'}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Attendees;
